import { Injectable, Type, ComponentFactoryResolver, Injector, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogModule } from './dialog.module';
import { GenericDialogComponent } from './generic-dialog/generic-dialog.component';
import { GenericDialogData } from './generic-dialog/generic-dialog-data';
import { GenericEntitySearchComponent } from './generic-entity-search/generic-entity-search.component';
import { GenericEntitySearchData } from './generic-entity-search/generic-entity-search-model';
import { GenericLogoutWarningDialogComponent } from './generic-logout-warning-dialog/generic-logout-warning-dialog.component';

@Injectable({
  providedIn: DialogModule,
})
export class DialogService {
  private dialog = inject(MatDialog);
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private injector = inject(Injector);

  public smallWidth = '50vw';
  public largeWidth = '80vw';
  public largeHeight: '90vh';

  
 

  

  showDiscardConfirmation() {
    const data: GenericDialogData = {
      title: 'Discard Draft',
      body:
        'Are you sure you want to discard this draft eForm?<br /><br /> Any changes you have made will not be saved.',
      buttons: [{ text: 'Yes, Discard Draft', value: true, default: true }, { text: 'No' }],
    };

    return this.showGenericDialog(data);
  }

  showCancelConfirmation() {
    const data: GenericDialogData = {
      title: 'Are you sure you want to discard your changes?',
      body: 'Any changes you have made will not be saved.',
      buttons: [
        { text: 'Yes, Discard Changes', value: true, default: true },
        { text: 'No, Go Back' },
      ],
    };

    return this.showGenericDialog(data);
  }

  showDeleteConfirmation(
    titleText = 'Are you sure you want to delete this record?',
    bodyText = '',
    deleteText = 'Delete',
  ) {
    const data: GenericDialogData = {
      title: titleText,
      body: bodyText,
      buttons: [{ text: deleteText, value: true, default: true }, { text: 'Cancel' }],
    };
    return this.showGenericDialog(data);
  }

  showSaveAsDraftConfirmation(
    titleText = 'Save as a Draft?',
    bodyText = 'Do you want to save your changes for later and exit the form?<br /><br />Unsubmitted draft forms will be removed 30 days after creation date.',
    saveButtonText = 'Yes, Save Draft',
    cancelButtonText = 'No, Keep Working',
  ) {
    const data: GenericDialogData = {
      title: titleText,
      body: bodyText,
      buttons: [
        { text: cancelButtonText, value: false },
        { text: saveButtonText, value: true, default: true },
      ],
    };

    return this.showGenericDialog(data);
  }

  showAttestationDialog() {
    const data: GenericDialogData = {
      title: 'Attestation and Submission',
      body:
        "By submission of this request for Prior Authorization I certify that:<br /><br /> (1) my statements are true and correct, <br /><br />(2) I have read and applied the Medical Treatment Guidelines to the care of this patient,<br /><br /> (3) the patient understands and agrees to undergo the proposed treatment or test, and <br /><br />(4) I do not have a substantially similar request pending. When a Health Care Provider submits a Prior Authorization Request to the Workers' Compensation Board using the provider's password, the provider agrees that this is equivalent to having placed his or her signature on the form, affirming the information contained herein.",
      buttons: [
        { text: 'Submit', value: true, default: true },
        { text: 'Cancel', value: false },
      ],
    };

    return this.showGenericDialog(data);
  }

  showGenericDialog(data: GenericDialogData) {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      data: data,
      width: data.width,
      maxWidth: data.maxWidth,
    });

    return dialogRef.afterClosed();
  }

  showCustomDialog<T>(componentType: Type<T>, data?: any, allowMultiple = true) {
    if (!allowMultiple) {
      this.closeOpenDialogs();
    }

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
    const componentRef = componentFactory.create(this.injector);
    const dialogRef = this.dialog.open<T>(componentRef.componentType, {
      data: data,
      width: data?.width || this.smallWidth,
    });

    return dialogRef.afterClosed();
  }

  openGenericEntitySearch(data: GenericEntitySearchData) {
    const config = {
      width: this.largeWidth,
      data: data,
    };
    const dialogRef = this.dialog.open(GenericEntitySearchComponent, config);

    return dialogRef.afterClosed();
  }

  showUnsavedConfirmation(userCanSave: boolean) {
    const data: GenericDialogData = {
      title: userCanSave ? 'There are unsaved changes.' : 'There are changes that cannot be saved.',
      body: 'Continue and lose changes?',
      buttons: [{ text: 'Continue', value: true, default: true }, { text: 'Cancel' }],
    };

    return this.showGenericDialog(data);
  }

  showExitConfirmation(userCanSave: boolean) {
    const data: GenericDialogData = {
      title: 'Are you sure you want to exit?',
      body: 'Are you sure you want to exit?',
      buttons: [
        { text: 'No, Go Back', value: 0 },
        { text: 'Exit Without Saving Changes', value: 2 },
      ],
    };

    if (userCanSave) {
      data.buttons.splice(1, 0, { text: 'Save and Exit', value: 1 });
    }

    return this.showGenericDialog(data);
  }

  showLogoutDialog() {
    return this.dialog.open(GenericLogoutWarningDialogComponent, {
      width: '30vw',
    });
  }

  showUnsavedWarningAfterIdleLogout() {
    const data: GenericDialogData = {
      title: 'You have been logged out due to inactivity.',
      body: 'Any unsaved changes have been lost.',
    };
    return this.showGenericDialog(data);
  }

  showUnsavedWarningAfterLogout() {
    const data: GenericDialogData = {
      title: 'You have been logged out.',
      body: 'Any unsaved changes have been lost.',
    };
    return this.showGenericDialog(data);
  }

  closeOpenDialogs() {
    if (this.dialog.openDialogs?.length) {
      this.dialog.closeAll();
    }
  }
}
